import React from 'react';
import '../../components/expense-table.scss';

interface ExpenseTableDetailSectionProps {
  linkedExpenses?: any[];
  title?: string;
}

export default function ExpenseTableDetailSection({
  linkedExpenses = [],
  title,
}: ExpenseTableDetailSectionProps) {
  if (!Array.isArray(linkedExpenses) || linkedExpenses.length === 0) {
    return null;
  }

  const resolvedTitle =
    title ||
    linkedExpenses[0]?.table_field_title ||
    linkedExpenses[0]?.table_field_display_name ||
    'Linked Expenses';

  // Collect all unique column headers from the custom_fields across all rows
  const columnHeaders: string[] = [];
  linkedExpenses.forEach((row) => {
    if (row.custom_fields && typeof row.custom_fields === 'object') {
      Object.keys(row.custom_fields).forEach((key) => {
        if (!columnHeaders.includes(key)) {
          columnHeaders.push(key);
        }
      });
    }
  });

  if (!columnHeaders.length) {
    return null;
  }

  const formatCellValue = (val: any) => {
    if (val === null || val === undefined || val === '') return '-';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) {
      return val
        .map((item) => (typeof item === 'object' ? item.name || item.text || item.label : item))
        .join(', ');
    }
    if (typeof val === 'object') {
      return val.name || val.text || val.label || val.display_name || JSON.stringify(val);
    }
    return String(val);
  };

  const isDurationColumn = (colName: string): boolean => {
    const norm = (colName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return norm.includes('duration') || norm === 'timediff' || norm === 'totaltime';
  };

  const parseDurationToMinutes = (val: any): number => {
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!isNaN(Number(trimmed))) return Number(trimmed);
      let totalMinutes = 0;
      const hoursMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*h(?:r|ours?)?/i);
      const minsMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*m(?:in|inutes?)?/i);
      if (hoursMatch) totalMinutes += parseFloat(hoursMatch[1]) * 60;
      if (minsMatch) totalMinutes += parseFloat(minsMatch[1]);
      if (!hoursMatch && !minsMatch) {
        const parts = trimmed.split(':');
        if (parts.length === 2 && !isNaN(Number(parts[0])) && !isNaN(Number(parts[1]))) {
          totalMinutes = Number(parts[0]) * 60 + Number(parts[1]);
        }
      }
      return totalMinutes;
    }
    return 0;
  };

  const formatDuration = (totalMinutes: number): string => {
    if (totalMinutes <= 0 || isNaN(totalMinutes)) return '0 mins';
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    if (hours > 0 && mins > 0) return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min${mins > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hr${hours > 1 ? 's' : ''}`;
    return `${mins} min${mins > 1 ? 's' : ''}`;
  };

  const durationColIndex = columnHeaders.findIndex(isDurationColumn);

  const totalDurationMinutes = durationColIndex !== -1
    ? linkedExpenses.reduce((acc, row) => {
        const durVal = row.custom_fields ? row.custom_fields[columnHeaders[durationColIndex]] : undefined;
        return acc + parseDurationToMinutes(durVal);
      }, 0)
    : 0;

  const hasTotals = durationColIndex !== -1 && totalDurationMinutes > 0;

  return (
    <div className="expense-table-detail-card">
      <div className="expense-table-detail-card__header">
        <h5>{resolvedTitle}</h5>
        <span className="badge">
          {linkedExpenses.length} {linkedExpenses.length === 1 ? 'Row' : 'Rows'}
        </span>
      </div>

      <div className="expense-table-detail-card__table-wrapper">
        <table>
          <thead>
            <tr>
              <th className="cell-index">#</th>
              {columnHeaders.map((colName) => (
                <th
                  key={colName}
                  style={{
                    textAlign: isDurationColumn(colName) ? 'center' : undefined,
                    minWidth: isDurationColumn(colName) ? '280px' : undefined,
                  }}
                >
                  {colName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linkedExpenses.map((row, idx) => (
              <tr key={row.id || idx}>
                <td className="cell-index">{row.row_no || idx + 1}</td>
                {columnHeaders.map((colName) => {
                  const val = row.custom_fields ? row.custom_fields[colName] : undefined;
                  return (
                    <td
                      key={colName}
                      style={{
                        textAlign: isDurationColumn(colName) ? 'center' : undefined,
                        fontWeight: isDurationColumn(colName) ? 500 : undefined,
                      }}
                    >
                      {formatCellValue(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {hasTotals && (
            <tfoot>
              <tr>
                <td className="cell-index"></td>
                {columnHeaders.map((colName, idx) => {
                  if (idx === durationColIndex) {
                    const totalHoursDecimal = (totalDurationMinutes / 60).toFixed(2);
                    const formatted = formatDuration(totalDurationMinutes);
                    return (
                      <td
                        key={colName}
                        className="total-value total-duration-value"
                        style={{
                          textAlign: 'center',
                          fontWeight: 700,
                          color: '#0f172a',
                          fontSize: '13px',
                          whiteSpace: 'nowrap',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          padding: '10px 14px',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline',
                            color: '#1e293b',
                            fontWeight: 700,
                            fontSize: '13px',
                            marginRight: '6px',
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            textOverflow: 'clip',
                          }}
                        >
                          Total Hours:
                        </span>
                        <span
                          style={{
                            display: 'inline',
                            color: '#0f172a',
                            fontWeight: 700,
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            textOverflow: 'clip',
                          }}
                        >
                          {totalHoursDecimal} hrs
                        </span>
                        <span
                          style={{
                            display: 'inline',
                            marginLeft: '6px',
                            color: '#0f172a',
                            fontWeight: 700,
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            textOverflow: 'clip',
                          }}
                        >
                          ({formatted})
                        </span>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={colName}
                      style={{
                        overflow: 'visible',
                        textOverflow: 'clip',
                      }}
                    />
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
